import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Button,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Layers as LayersIcon,
  SatelliteAlt as SatelliteIcon,
  Map as MapIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { assetsApi } from '../../../services/api';

function formatCaptureDate(isoStr) {
  if (!isoStr) return 'Unknown date';
  return new Date(isoStr).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/**
 * AssetGisDataPanel
 *
 * GIS data browser for a single asset, opened from the "GIS Data" button in the
 * asset detail drawer. Currently surfaces orthophoto captures (aerial raster
 * pyramids). Selecting a capture overlays it on the main Cesium map and flies the
 * camera to its footprint; the layout is sectioned so other GIS sources (e.g.
 * organization GeoJSON layers) can be added later.
 *
 * Props:
 *  - open (bool), onClose (fn), asset (object)
 *  - onSelectOrthophoto (fn): called with a capture descriptor to overlay, or null to remove.
 *      descriptor = { organization_id, asset_id, date, label, bounds, minzoom, maxzoom }
 *  - selectedOrthophoto (object|null): the capture currently overlaid on the map.
 */
const AssetGisDataPanel = ({ open, onClose, asset, onSelectOrthophoto, selectedOrthophoto }) => {
  const theme = useTheme();
  const [orthophotos, setOrthophotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOrthophotos = useCallback(async () => {
    if (!asset?.id) return;
    setLoading(true);
    try {
      const response = await assetsApi.getAssetOrthophotos(asset.id);
      setOrthophotos(response.data?.available || []);
    } catch (error) {
      console.error('Error fetching orthophotos:', error);
      setOrthophotos([]);
    } finally {
      setLoading(false);
    }
  }, [asset?.id]);

  useEffect(() => {
    if (open && asset?.id) {
      fetchOrthophotos();
    }
  }, [open, asset?.id, fetchOrthophotos]);

  const assetId = asset?.id || asset?.assetId;
  const orgId = asset?.organization_id || asset?.organizationId;

  // Which capture (if any) is currently overlaid for THIS asset
  const activeCaptureName =
    selectedOrthophoto && String(selectedOrthophoto.asset_id) === String(assetId)
      ? selectedOrthophoto.date
      : null;

  const handleToggle = (capture) => {
    if (activeCaptureName === capture.name) {
      // Toggle off — remove the overlay, keep the panel open to pick another
      onSelectOrthophoto(null);
    } else {
      onSelectOrthophoto({
        organization_id: orgId,
        asset_id: assetId,
        date: capture.name,
        label: formatCaptureDate(capture.date),
        bounds: capture.bounds,
        minzoom: capture.minzoom,
        maxzoom: capture.maxzoom,
      });
      // Close so the overlay + camera fly-to are visible on the map
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '75vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        pb: 1, borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LayersIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            GIS Data
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, minHeight: 240 }}>
        {/* Section: Orthophotos */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2.5, pt: 2, pb: 1.5,
        }}>
          <SatelliteIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Orthophotos
          </Typography>
          {!loading && (
            <Chip label={orthophotos.length} size="small" color="primary" variant="outlined" />
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : orthophotos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <SatelliteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No orthophotos available for this asset.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {orthophotos.map((capture, index) => {
              const isActive = activeCaptureName === capture.name;
              return (
                <React.Fragment key={capture.name}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      py: 1.5, px: 2.5,
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <SatelliteIcon sx={{ color: isActive ? theme.palette.primary.main : '#1976d2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatCaptureDate(capture.date)}
                          </Typography>
                          {isActive && (
                            <Chip label="On map" size="small" color="primary" sx={{ height: 20 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Aerial orthophoto
                          {capture.minzoom != null && capture.maxzoom != null
                            ? ` • zoom ${capture.minzoom}–${capture.maxzoom}`
                            : ''}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant={isActive ? 'contained' : 'outlined'}
                        color="primary"
                        startIcon={isActive ? <VisibilityOffIcon fontSize="small" /> : <MapIcon fontSize="small" />}
                        onClick={() => handleToggle(capture)}
                        sx={{ textTransform: 'none' }}
                      >
                        {isActive ? 'Hide' : 'Show on map'}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssetGisDataPanel;
